import type InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import { createSignal } from '@verclam/pulsar';

/**
 * The panel mounts outside the screen's tree and can receive no prop: every identifier it needs to
 * drive the three assignment commands rides along in this context. `facilityId` is not carried here —
 * the panel reads it from `useStrictCurrentFacility()`, since the screen is mounted under
 * `RequireFacility`.
 *
 * `participants` looks like it breaks the "primitives only" rule (`guides/signals.md`), but what that
 * rule forbids is a store View — an object coupled to the store, carrying methods and fields the panel
 * never needs, that can go stale as the store moves on underneath it. Each entry is a flat,
 * three-field record with no method and no dependency on the store: the screen owns
 * `detail.participants` (already fused local + server) and hands this signal a one-time snapshot of
 * it, not a live reference.
 * `interviewsStore.interview` is never filled on this screen, so this is the only channel able to
 * carry them into a sidebar mounted at the root of the feature.
 */
export type SpeakerAssignmentContext = {
	readonly speakerId: number;
	readonly subjectTypeKey: string;
	readonly subjectId: number;
	readonly interviewId: number;
	readonly participants: readonly InternalInterviewParticipantView[];
};

const speakerAssignmentSignal = createSignal<SpeakerAssignmentContext | null>(null);

export const setSpeakerAssignmentContext = (context: SpeakerAssignmentContext | null): void => {
	speakerAssignmentSignal.value = context;
};

export default speakerAssignmentSignal;
